import React, { useState, useEffect, useRef } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import ReactMarkdown from"react-markdown";
import remarkGfm from"remark-gfm";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";

// If you already expose LANGUAGE_MAP to FE, import it from your utils.
// Fallback map here so this file is self-contained.
const LANGUAGE_MAP = {
 en:"English",
 hi:"Hindi",
 ta:"Tamil",
 te:"Telugu",
 kn:"Kannada",
 ml:"Malayalam",
 mr:"Marathi",
 gu:"Gujarati",
 bn:"Bengali",
 pa:"Punjabi",
 ur:"Urdu",
};

const CodeReviewPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 if (isTenantMode) {
   return <CodeReviewPageContent mode={mode} />;
 }

 return (
   <FeatureGuard feature="TSG_AI">
     <CodeReviewPageContent mode={mode} />
   </FeatureGuard>
 );
};

const CodeReviewPageContent = ({ mode = "learner" }) => {
 const [input, setInput] = useState("");
 const [chat, setChat] = useState([]);
 const [loading, setLoading] = useState(false);
 const chatEndRef = useRef(null);
 const navigate = useNavigate();

 const isTenantMode = mode === "tenant";

 // language from navigation state OR saved session
 const location = useLocation();
 const { session } = useSkillGenieSession();
 const langCode = (location.state?.language || session?.language ||"en").toLowerCase();
 const langName = LANGUAGE_MAP[langCode] ||"English";

 const handleAsk = async () => {
 if (!input.trim()) return;

 const user = JSON.parse(localStorage.getItem("user") ||"{}");
 const userId = user?.userId ||"UnknownUser";

 if (!userId || userId ==="UnknownUser") {
 alert("Please log in to use this feature.");
 navigate("/login");
 return;
 }

    const currentInput = input;
    setChat((prev) => [...prev, { role: "user", content: currentInput }]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/tsg/code_review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          prompt: currentInput,
          chatHistory: chat,
          language: langCode,
          technology: "general",
          topic: "general",
        }),
      });

      const data = await res.json();
      const reply = data.reply || "No reply.";
      setChat((prev) => [
        ...prev,
        { role: "assistant", content: reply },
      ]);
    } catch {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." },
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  return (
    <div className="p-4 md:p-8 min-h-screen bg-[#F8FAFC] text-[#111827] font-sans">
      <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-120px)] premium-card p-6 md:p-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
              title="Go Back"
            >
              <ChevronLeftIcon className="w-6 h-6 text-[#5B21B6] group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="p-3 bg-purple-50 rounded-xl border-2 border-[#5B21B6] shadow-sm">
              <svg className="w-8 h-8 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-black text-[#111827] uppercase tracking-tight">
                @tsg <span className="text-[#5B21B6]">→</span> Code Review
              </h2>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">AI-powered technical code analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-purple-100 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">lang: {langName}</span>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto mb-6 pr-2 custom-scrollbar space-y-6">
          {chat.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 opacity-40">
              <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100">
                <svg className="w-10 h-10 text-[#5B21B6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="font-bold uppercase tracking-widest text-sm text-[#111827]">Paste your code for review</p>
                <p className="text-xs font-bold text-gray-400 mt-1 uppercase tracking-tighter">I'll find bugs, suggest improvements and optimize your code.</p>
              </div>
            </div>
          ) : (
            chat.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[75%] p-5 rounded-[24px] shadow-sm border-2 transition-all ${
                    msg.role === "user"
                      ? "bg-[#5B21B6] text-white rounded-tr-none border-[#5B21B6] shadow-purple-100"
                      : "bg-[#F9F7FF] text-[#111827] rounded-tl-none border-[#5B21B6] shadow-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-black uppercase tracking-wider ${msg.role === "user" ? "text-purple-200" : "text-gray-400"}`}>
                      {msg.role === "user" ? "You" : "TSG Code Expert"}
                    </span>
                  </div>
                  <div className={`prose max-w-none prose-p:leading-relaxed ${msg.role === 'user' ? 'prose-p:text-white prose-headings:text-white prose-strong:text-white prose-code:text-white' : 'prose-p:text-gray-600 prose-headings:text-[#5B21B6] prose-strong:text-[#5B21B6]'}`}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => <p {...props} className="mb-0" />,
                        code: ({ node, inline, ...props }) => (
                          <code
                            {...props}
                            className={`${
                              inline
                                ? (msg.role === 'user' ? "bg-white/20 text-white px-1.5 py-0.5 rounded text-sm font-bold" : "bg-purple-50 text-[#5B21B6] px-1.5 py-0.5 rounded text-sm font-bold")
                                : "block bg-gray-900 text-green-400 p-4 rounded-xl border border-gray-800 font-mono text-sm my-4 overflow-x-auto"
                            }`}
                          />
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Sticky Input Area */}
        <div className="relative">
          {loading && (
            <div className="absolute -top-10 left-4 flex items-center gap-2 text-[#5B21B6] text-[10px] font-black uppercase tracking-widest">
              <span className="flex space-x-1">
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span className="w-1.5 h-1.5 bg-[#5B21B6] rounded-full animate-bounce"></span>
              </span>
              TSG is analyzing code...
            </div>
          )}
          <div className="flex flex-col gap-3 p-4 bg-[#F9F7FF] rounded-[24px] border-2 border-[#5B21B6] shadow-xl shadow-purple-100 focus-within:ring-4 focus-within:ring-purple-50 transition-all">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste code or ask for review..."
              className="w-full bg-transparent px-4 py-2 text-sm font-bold text-[#111827] placeholder-gray-400 focus:outline-none min-h-[150px] resize-none"
            />
            <div className="flex justify-end">
              <button
                onClick={handleAsk}
                disabled={loading || !input.trim()}
                className="p-4 rounded-2xl bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white hover:scale-105 active:scale-95 transition-all disabled:opacity-30 disabled:hover:scale-100 shadow-lg shadow-purple-200"
              >
                <svg className={`w-6 h-6 transform ${loading ? 'animate-spin' : 'hover:translate-x-1 hover:-translate-y-1'} transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
          <p className="mt-4 text-center text-[10px] font-black text-[#94A3B8] uppercase tracking-widest">
             AI-POWERED BY THE SKILLGEN ELITE MASTERY SYSTEM
          </p>
        </div>
      </div>
    </div>
  );
};

export default CodeReviewPage;



